/**
 * Cart free gift widget — Prestige
 * -----------------------------------------------------------------
 * The Liquid template renders the widget in its correct state using
 * the cart as it was on the server. This script:
 *   1. Normalises the cart into the SHOP's currency (see below).
 *   2. Re-syncs the widget state after an AJAX cart change.
 *   3. Adds the chosen gift variant, after re-verifying the live cart.
 *   4. Removes the gift if the cart drops back below the threshold.
 *
 * CURRENCY
 * The threshold in theme settings is entered in the shop's own
 * currency. Cart totals arrive in the customer's presentment currency.
 * Everything is converted DOWN into shop currency in one place —
 * toShopCurrency() — and every comparison happens there. The threshold
 * is never modified. Amounts are converted back up only for display.
 *
 * It never un-hides anything unless it holds verified cart data, so a
 * failed or slow /cart.js request leaves the server-rendered state in
 * place rather than falling open.
 *
 * The Shopify "Buy X get Y" automatic discount is what zeroes the
 * price — this script never touches pricing.
 */
(function () {
  if (window.__cartFreeGiftInit) return;
  window.__cartFreeGiftInit = true;

  var GIFT_PROPERTY = '_free_gift';
  var rootUrl = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');

  var busy = false;
  var lastSync = 0;

  /* ----------------------------- helpers ----------------------------- */

  function widget() {
    return document.querySelector('[data-free-gift]');
  }

  function request(path, payload) {
    return fetch(rootUrl + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw data;
        return data;
      });
    });
  }

  function getCart() {
    return fetch(rootUrl + '/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (r) {
      return r.json();
    });
  }

  function isCart(value) {
    return !!(value && Array.isArray(value.items));
  }

  /** Tell Prestige to re-render the cart drawer. */
  function refreshCart() {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true })
    );
  }

  function setError(el, message) {
    var target = el.querySelector('[data-gift-error]');
    if (!target) return;
    if (message) {
      target.textContent = message;
      target.hidden = false;
    } else {
      target.hidden = true;
    }
  }

  /* ---------------------- currency normalisation ---------------------- */

  /**
   * Rate from the shop's currency to the currency being displayed.
   * Falls back to 1 on a single-currency store, or if Shopify has not
   * published a rate — in which case no conversion happens and the
   * comparison is already like-for-like.
   */
  function currencyRate() {
    var rate =
      window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    return rate && isFinite(rate) && rate > 0 ? rate : 1;
  }

  /** Presentment cents -> shop cents. Every condition runs on this. */
  function toShopCurrency(cents) {
    return cents / currencyRate();
  }

  /** Shop cents -> presentment cents. Display only. */
  function toCartCurrency(cents) {
    return cents * currencyRate();
  }

  function formatMoney(cents, el) {
    var active = el.getAttribute('data-cart-currency');
    var base = el.getAttribute('data-shop-currency');

    // Converted currency: the shop's money_format carries the wrong
    // symbol, so fall back to the browser's own currency formatting.
    if (active && base && active !== base && window.Intl && window.Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: active
        }).format(cents / 100);
      } catch (e) {
        /* fall through */
      }
    }

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = el.getAttribute('data-money-format') || '${{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) return '0';

      var parts = (number / 100.0).toFixed(precision).split('.');
      var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return head + (parts[1] ? decimal + parts[1] : '');
    }

    var value;
    switch ((fmt.match(placeholder) || [])[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    return fmt.replace(placeholder, value);
  }

  /* ------------------------ eligibility maths ------------------------ */

  /**
   * Qualifying subtotal, IN SHOP CURRENCY.
   * Every line except this offer's gift line. original_line_price is
   * used so the Buy X get Y discount allocated to the gift can never
   * drag the cart back under the threshold.
   */
  function qualifyingTotal(cart, offerId) {
    var presentment = cart.items.reduce(function (sum, item) {
      var props = item.properties || {};
      if (props[GIFT_PROPERTY] === offerId) return sum;
      var price = item.original_line_price;
      if (price == null) price = item.line_price;
      if (price == null) price = 0;
      return sum + price;
    }, 0);

    return toShopCurrency(presentment);
  }

  function findGiftLine(cart, offerId) {
    for (var i = 0; i < cart.items.length; i++) {
      var props = cart.items[i].properties || {};
      if (props[GIFT_PROPERTY] === offerId) return cart.items[i];
    }
    return null;
  }

  function readConfig(el) {
    // Threshold stays exactly as the merchant entered it, in shop cents.
    var threshold = parseInt(el.getAttribute('data-threshold'), 10);
    return {
      offerId: el.getAttribute('data-offer-id'),
      // A missing or unparseable threshold must never mean "always eligible".
      threshold: isNaN(threshold) ? Infinity : threshold,
      showProgress: el.getAttribute('data-show-progress') === 'true',
      autoRemove: el.getAttribute('data-auto-remove') === 'true'
    };
  }

  /* --------------------------- show / hide --------------------------- */

  function render(el, cart) {
    if (!isCart(cart)) return;

    var config = readConfig(el);
    var total = qualifyingTotal(cart, config.offerId); // shop currency
    var giftLine = findGiftLine(cart, config.offerId);
    var eligible = total >= config.threshold;

    var unlocked = el.querySelector('[data-gift-state="unlocked"]');
    var locked = el.querySelector('[data-gift-state="locked"]');
    var form = el.querySelector('[data-gift-form]');
    var added = el.querySelector('[data-gift-added]');
    var button = el.querySelector('[data-gift-add]');

    // Cart fell below the threshold while the gift is still in it.
    if (!eligible && giftLine && config.autoRemove && !busy) {
      busy = true;
      request('/cart/change.js', { id: giftLine.key, quantity: 0 })
        .then(refreshCart)
        .catch(function () {})
        .then(function () {
          busy = false;
        });
      return;
    }

    el.hidden = !(eligible || config.showProgress);
    if (unlocked) unlocked.hidden = !eligible;
    if (locked) locked.hidden = eligible || !config.showProgress;

    // Second line of defence: the button is only ever enabled when the
    // live cart qualifies, whatever state the markup arrived in.
    if (button) {
      if (eligible) {
        button.removeAttribute('disabled');
      } else {
        button.setAttribute('disabled', 'disabled');
      }
    }

    if (eligible) {
      if (form) form.hidden = !!giftLine;
      if (added) added.hidden = !giftLine;
      setError(el, null);
    } else if (config.showProgress) {
      var progress = el.querySelector('[data-gift-progress]');
      var bar = el.querySelector('[data-gift-bar]');

      if (progress) {
        // Shortfall is worked out in shop currency, then converted back
        // so the customer reads it in the currency they are shopping in.
        var remaining = Math.max(config.threshold - total, 0);
        progress.textContent = (el.getAttribute('data-progress-text') || '').replace(
          '[remaining]',
          formatMoney(Math.round(toCartCurrency(remaining)), el)
        );
      }
      if (bar && isFinite(config.threshold) && config.threshold > 0) {
        bar.style.width = Math.min((total / config.threshold) * 100, 100) + '%';
      }
    }
  }

  function sync(cart) {
    var el = widget();
    if (!el) return;

    if (isCart(cart)) {
      render(el, cart);
      return;
    }

    var now = Date.now();
    if (now - lastSync < 250) return;
    lastSync = now;

    getCart()
      .then(function (data) {
        var current = widget();
        if (current) render(current, data);
      })
      .catch(function () {
        /* leave the server-rendered state alone */
      });
  }

  /* ------------------------------ add to cart ------------------------- */

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-gift-add]');
    if (!button || busy || button.hasAttribute('disabled')) return;

    event.preventDefault();

    var el = button.closest('[data-free-gift]');
    if (!el) return;

    var config = readConfig(el);
    var input = el.querySelector('[data-gift-variant]');
    var variantId = input && input.value;

    setError(el, null);

    if (!variantId) {
      setError(el, 'Please choose an option.');
      return;
    }

    busy = true;
    button.setAttribute('disabled', 'disabled');
    button.classList.add('is-loading');

    // Re-verify against the live cart before adding. This is the guard
    // that makes it impossible to claim the gift below the minimum,
    // however the widget got into the state the customer is looking at.
    getCart()
      .then(function (cart) {
        if (!isCart(cart)) throw new Error('unavailable');

        if (findGiftLine(cart, config.offerId)) {
          return null; // already in the cart, nothing to do
        }

        if (qualifyingTotal(cart, config.offerId) < config.threshold) {
          var error = new Error('not eligible');
          error.notEligible = true;
          error.cart = cart;
          throw error;
        }

        var properties = {};
        properties[GIFT_PROPERTY] = config.offerId;

        return request('/cart/add.js', {
          items: [{ id: Number(variantId), quantity: 1, properties: properties }]
        });
      })
      .then(function () {
        refreshCart();
      })
      .catch(function (error) {
        if (error && error.notEligible) {
          // Repaint from the real cart so the customer sees the progress bar.
          render(el, error.cart);
          return;
        }
        setError(
          el,
          (error && (error.description || error.message)) ||
            'Sorry, this gift could not be added.'
        );
      })
      .then(function () {
        busy = false;
        button.classList.remove('is-loading');
        sync();
      });
  });

  /* ------------------------------- triggers --------------------------- */

  // Prestige dispatches these on the root element after any cart change.
  ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function (name) {
    document.documentElement.addEventListener(name, function (event) {
      var detail = event && event.detail;
      var cart = detail && (detail.cart || detail);
      sync(isCart(cart) ? cart : null);
    });
  });

  // Fallback: the drawer being re-rendered is itself a signal.
  var debounce;
  new MutationObserver(function () {
    clearTimeout(debounce);
    debounce = setTimeout(function () {
      sync();
    }, 200);
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      sync();
    });
  } else {
    sync();
  }
})();